import React from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { adminColors } from '../../styles/adminDesignTokens';
import { adminAutocompleteSx, adminCombineSx } from '../../styles/adminComponentStyles';

type AdminMultiValueAutocompleteProps = {
  ariaLabel: string;
  dataOptions: string[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string;
  name?: string;
  onBlur?: () => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
};

const normalizeAdminMultiValue = (value: unknown): string => String(value ?? '').trim();

const dedupeAdminMultiValues = (values: readonly unknown[]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];

  values.forEach((value) => {
    const normalizedValue = normalizeAdminMultiValue(value);
    if (!normalizedValue || seen.has(normalizedValue)) {
      return;
    }
    seen.add(normalizedValue);
    merged.push(normalizedValue);
  });

  return merged;
};

const getOrderedAdminMultiOptions = (dataOptions: string[], selectedValues: string[]): string[] => {
  const normalizedDataOptions = dedupeAdminMultiValues(dataOptions);
  const normalizedSelectedValues = dedupeAdminMultiValues(selectedValues);
  const selectedSet = new Set(normalizedSelectedValues);

  return [
    ...normalizedSelectedValues,
    ...normalizedDataOptions.filter((option) => !selectedSet.has(option)),
  ];
};

const filterAdminMultiOptions = (options: string[], inputValue: string): string[] => {
  const query = normalizeAdminMultiValue(inputValue);
  const filteredOptions = query
    ? options.filter((option) => option.toLowerCase().includes(query.toLowerCase()))
    : options;

  if (query && !options.includes(query)) {
    return [...filteredOptions, query];
  }

  return filteredOptions;
};

const AdminMultiValueAutocomplete: React.FC<AdminMultiValueAutocompleteProps> = ({
  ariaLabel,
  dataOptions,
  error = false,
  helperText,
  inputRef,
  label,
  name,
  onBlur,
  onChange,
  placeholder = 'Search or add value',
  sx,
  value,
}) => {
  const selectedValues = React.useMemo(() => dedupeAdminMultiValues(value), [value]);
  const normalizedDataOptions = React.useMemo(() => dedupeAdminMultiValues(dataOptions), [dataOptions]);
  const orderedOptions = React.useMemo(
    () => getOrderedAdminMultiOptions(normalizedDataOptions, selectedValues),
    [normalizedDataOptions, selectedValues],
  );
  const isDataOption = React.useCallback(
    (option: string) => normalizedDataOptions.includes(option),
    [normalizedDataOptions],
  );
  const handleChange = React.useCallback(
    (_event: React.SyntheticEvent, nextValue: Array<string>) => onChange(dedupeAdminMultiValues(nextValue)),
    [onChange],
  );
  const handleFilterOptions = React.useCallback(
    (options: string[], state: { inputValue: string }) => filterAdminMultiOptions(options, state.inputValue),
    [],
  );

  return (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      disableCloseOnSelect
      size="small"
      options={orderedOptions}
      value={selectedValues}
      onBlur={onBlur}
      onChange={handleChange}
      filterOptions={handleFilterOptions}
      getOptionLabel={(option) => option}
      isOptionEqualToValue={(option, selectedOption) => option === selectedOption}
      noOptionsText="Type a value to add it"
      renderTags={(tagValue, getTagProps) => (
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const customValue = !isDataOption(option);
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={option}
              icon={customValue ? (
                <WarningAmberRoundedIcon
                  color="warning"
                  fontSize="small"
                  titleAccess="Not found in POS data"
                />
              ) : undefined}
            />
          );
        })
      )}
      renderOption={(props, option, { selected }) => {
        const customValue = !isDataOption(option);
        return (
          <Box
            component="li"
            {...props}
            key={option}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              py: 0.75,
            }}
          >
            <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              <Typography component="span" sx={{ display: 'block', fontSize: '13px' }}>
                {option}
              </Typography>
              {customValue ? (
                <Typography component="span" sx={{ color: adminColors.warning, display: 'block', fontSize: '11px' }}>
                  Not found in POS data
                </Typography>
              ) : null}
            </Box>
            {customValue ? (
              <Tooltip title="This value is not in the POS data returned for this store">
                <WarningAmberRoundedIcon color="warning" fontSize="small" />
              </Tooltip>
            ) : null}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          inputRef={inputRef}
          label={label}
          name={name}
          placeholder={selectedValues.length > 0 ? '' : placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': ariaLabel,
          }}
        />
      )}
      sx={adminCombineSx(adminAutocompleteSx, sx)}
    />
  );
};

export { dedupeAdminMultiValues, getOrderedAdminMultiOptions };
export default React.memo(AdminMultiValueAutocomplete);
